import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError } from "../../api/http";
import { useAuth } from "../../auth/AuthContext";
import { Button, Input, PasswordInput, Label } from "../../components/ui";
import { useT } from "../../i18n";
import { AuthLayout } from "./AuthLayout";

/** Self-serve signup: name + email + password, nothing more. We deliberately
 *  sell nothing here — no plan picker, no price. On success the server logs
 *  the learner straight in and we send them into onboarding, where the
 *  questionnaire produces a recommendation. */
export function SignUpPage() {
  const t = useT();
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signup(email, password, fullName);
      // The onboarding guard (S1) will hold un-onboarded users here anyway;
      // aiming at /onboarding makes the intent explicit.
      navigate("/onboarding");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("auth.signup.title")} subtitle={t("auth.signup.subtitle")}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <Label htmlFor="full_name">{t("auth.name")}</Label>
          <Input
            id="full_name"
            type="text"
            autoComplete="name"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            showLabel={t("common.showPassword")}
            hideLabel={t("common.hidePassword")}
          />
          <p className="mt-1 text-xs text-muted">{t("auth.passwordHint")}</p>
        </div>
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        <Button type="submit" loading={busy} className="w-full">
          {t("auth.createAccount")}
        </Button>
        <p className="text-center text-sm text-muted">
          {t("auth.haveAccount")}{" "}
          <Link to="/login" className="font-medium text-brand hover:underline">
            {t("auth.login")}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
